// Next-anniversary progress-bar helper (W3.1 §5.6 segment 2).
//
// Computes the "how far through the cycle are we" percentage, used by
// `NextAnniversaryCountdown` to fill the 4px progress bar under the days
// number.
//
// Per spec §5.6 segment 2:
//   • anchor anniversary → 365-day cycle (today → next occurrence)
//   • non-anchor recurring → "上一個 → 下一個" cycle (one year)
//   • one-shot (is_recurring=false) → always 100% (we're counting down to a
//     single fixed point)
//
// Edge cases (mirror spec §5.6):
//   • 今天 → 100% (just hit the milestone)
//   • 明天 → ~99.7%
//   • > 365 天 → caller should switch to "year" dimension

import { differenceInCalendarDays, parseISO } from "date-fns";

export interface ProgressBarInput {
  /** Anchor anniversaries cycle on 365 days. */
  isAnchor: boolean;
  /** When false, the row is one-shot (single target date). */
  isRecurring: boolean;
  /** Last occurred date (ISO). Used by non-anchor cycle. */
  previousOccurrenceIso?: string | null;
  /** Next occurrence date (ISO). Required. */
  nextOccurrenceIso: string;
  /** Today's local date in HKT (defaults to `new Date()`). */
  now?: Date;
}

export interface ProgressBarResult {
  /** 0..100 inclusive. */
  percent: number;
  /** Human-readable label for the progress bar (e.g. 「N 天 → 下一個」). */
  label: string;
  /** True when caller should switch to year-scale rendering. */
  isYearScale: boolean;
}

const YEARLY_CYCLE_DAYS = 365;

/**
 * Compute the progress-bar percent + label for a "next anniversary" row.
 * Pure — no DOM / no React.
 */
export function progressBar(input: ProgressBarInput): ProgressBarResult {
  const now = input.now ?? new Date();
  const next = parseISO(input.nextOccurrenceIso);
  const days = differenceInCalendarDays(next, now);

  // Non-recurring: we count down to a single point. Bar fills as we approach.
  if (!input.isRecurring) {
    if (days <= 0) {
      return { percent: 100, label: "今天", isYearScale: false };
    }
    // 30-day cap: the one-shot is treated as a 30-day cycle where progress
    // = (cap - days) / cap, so the bar fills as we get closer.
    const cap = 30;
    const pct = Math.min(100, Math.max(0, ((cap - days) / cap) * 100));
    return { percent: Math.round(pct), label: `${days} 天`, isYearScale: false };
  }

  // Recurring.
  if (days <= 0) {
    return { percent: 100, label: "今天", isYearScale: false };
  }
  if (days > YEARLY_CYCLE_DAYS) {
    // Year-scale — caller should render ">1年" instead of days.
    const years = Math.round(days / YEARLY_CYCLE_DAYS);
    return { percent: 100, label: `${years} 年`, isYearScale: true };
  }

  let cycleLength = YEARLY_CYCLE_DAYS;
  let cycleElapsed = YEARLY_CYCLE_DAYS - days;
  if (input.isAnchor) {
    cycleLength = YEARLY_CYCLE_DAYS;
    cycleElapsed = YEARLY_CYCLE_DAYS - days;
  } else if (input.previousOccurrenceIso) {
    const prev = parseISO(input.previousOccurrenceIso);
    if (!Number.isNaN(prev.getTime())) {
      cycleLength = Math.max(1, differenceInCalendarDays(next, prev));
      cycleElapsed = Math.max(0, differenceInCalendarDays(now, prev));
    }
  }
  // Ratio within the cycle: 0% at the start of a cycle (just past last
  // occurrence), 100% when we hit the next one. Spec §5.6 segment 2:
  // progress = (year_cycle - days_until) / year_cycle × 100. For
  // days_until=364 the bar reads 0.27% — i.e. we're nearly at the start of
  // the new cycle, bar mostly empty.
  const ratio = cycleLength > 0 ? Math.min(1, Math.max(0, cycleElapsed / cycleLength)) : 1;
  return {
    percent: Math.round(ratio * 100),
    label: `${days} 天`,
    isYearScale: false,
  };
}